import {
    ActivityCategory,
    activityCategoryLabels,
    ActivityRecordType,
    TaskTypeId,
} from "@sp-crm/core";
import { Select } from "components/ui/select";
import React from "react";
import { useFeature } from "store/selectors/hooks";
import {
    activityTypeChoicesForRecordType,
    ActivityTypeOption,
    byActivityTypeName,
    noActivityTypeKey,
    unscopedVisitTypeChoices,
    useActivityTypeOptions,
    visitHostingRecordTypes,
} from "./activity-type-options";
import { useAvailableActivityCategories } from "./use-activity-categories";

interface ActivityTypeSelectProps {
    value: TaskTypeId | null;
    recordType: ActivityRecordType | null;
    onChange: (value: TaskTypeId | null) => void;
}

export const ActivityTypeSelect: React.FC<ActivityTypeSelectProps> = props => {
    const { value, recordType, onChange } = props;
    const options = useActivityTypeOptions();
    const availableCategories = useAvailableActivityCategories();
    const referralDetailVNext = useFeature("referralDetailVNext");
    const homeCareLeadDetailVNext = useFeature("homeCareLeadDetailVNext");
    const visitHosting = visitHostingRecordTypes({
        referralDetailVNext,
        homeCareLeadDetailVNext,
    });
    const retainedVisitChoices = (): ActivityTypeOption[] =>
        options.filter(option => option.category === "visit" && option.id === value);
    const choicesFor = (category: ActivityCategory): ActivityTypeOption[] =>
        activityTypeChoicesForRecordType(options, category, recordType, value);
    const visitsRetainedOnly = recordType !== null && visitHosting.includes(recordType);
    const visitChoices = (): ActivityTypeOption[] => {
        if (visitsRetainedOnly) {
            return retainedVisitChoices();
        }
        if (recordType === null && visitHosting.length > 0) {
            return unscopedVisitTypeChoices(options, value, visitHosting);
        }
        return choicesFor("visit");
    };

    return (
        <Select
            label="Activity type"
            value={value ?? noActivityTypeKey}
            onChange={e =>
                onChange(
                    e.target.value === noActivityTypeKey
                        ? null
                        : (e.target.value as TaskTypeId),
                )
            }>
            <option value={noActivityTypeKey}>(none)</option>
            {availableCategories.map(category => {
                const choices = (
                    category === "visit" ? visitChoices() : choicesFor(category)
                ).sort(byActivityTypeName);
                if (choices.length === 0) {
                    return null;
                }
                return (
                    <optgroup key={category} label={activityCategoryLabels[category]}>
                        {choices.map(option => (
                            <option
                                key={option.id}
                                value={option.id}
                                disabled={category === "visit" && visitsRetainedOnly}>
                                {option.name}
                            </option>
                        ))}
                    </optgroup>
                );
            })}
        </Select>
    );
};
